import { safeStorage } from '../helpers/safeStorage';

const channels = new Map();
const viewsByCenter = new WeakMap();
const initializedPanels = new WeakSet();

function normalizeIds(ids) {
    return [...new Set((Array.isArray(ids) ? ids : []).map(String).filter(Boolean))];
}

function normalizeState(state = {}) {
    const dismissed = normalizeIds(state.dismissed);
    const read = normalizeIds(state.read).filter((id) => !dismissed.includes(id));

    return { read, dismissed };
}

function mergeStates(current, incoming) {
    const dismissed = normalizeIds([...current.dismissed, ...(incoming.dismissed || [])]);
    const read = normalizeIds([...current.read, ...(incoming.read || [])])
        .filter((id) => !dismissed.includes(id));

    return { read, dismissed };
}

function readLegacyState(storageKey) {
    if (!storageKey) {
        return null;
    }

    try {
        const state = JSON.parse(safeStorage.local.get(storageKey) || 'null');
        return state && typeof state === 'object' ? normalizeState(state) : null;
    } catch (error) {
        return null;
    }
}

function stateFromCenter(center) {
    const read = [...center.querySelectorAll('[data-notification-item][data-notification-state-managed="1"][data-notification-read="1"]')]
        .map((item) => item.dataset.notificationId);

    return normalizeState({ read, dismissed: [] });
}

function syncView(view) {
    const { channel, count, summary, empty, items } = view;
    let visibleCount = 0;
    let unreadCount = 0;

    items.forEach((item) => {
        const id = item.dataset.notificationId;
        const stateManaged = item.dataset.notificationStateManaged !== '0';
        const dismissed = stateManaged && channel.state.dismissed.includes(id);
        item.hidden = dismissed;

        if (!dismissed) {
            visibleCount++;
            unreadCount += stateManaged && channel.state.read.includes(id) ? 0 : 1;
        }
    });

    count.textContent = String(unreadCount);
    count.hidden = unreadCount === 0;
    empty.hidden = visibleCount !== 0;
    summary.textContent = channel.error
        || (unreadCount ? `${unreadCount} unread` : 'All caught up');
}

function syncChannel(channel) {
    channel.views.forEach(syncView);
}

function publishState(channel) {
    syncChannel(channel);
    channel.broadcast?.postMessage(channel.state);
}

function appendIds(params, key, ids) {
    normalizeIds(ids).forEach((id) => params.append(`${key}[]`, id));
}

function replaceChannelItems(channel, html) {
    if (typeof html !== 'string') {
        return;
    }

    const itemsByList = new Map();

    channel.views.forEach((view) => {
        if (!itemsByList.has(view.list)) {
            view.list.innerHTML = html;
            itemsByList.set(view.list, [...view.list.querySelectorAll('[data-notification-item]')]);
        }

        view.items = itemsByList.get(view.list);
    });
}

function queueMutation(channel, operation, payload = {}, onSuccess = null, silent = false) {
    const run = async () => {
        const params = new URLSearchParams({
            action: channel.action,
            nonce: channel.nonce,
            operation,
        });

        appendIds(params, 'ids', payload.ids);
        appendIds(params, 'read_ids', payload.read);
        appendIds(params, 'dismissed_ids', payload.dismissed);

        const response = await fetch(channel.ajaxUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
            body: params.toString(),
        });
        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(result?.data?.message || 'Notification state could not be saved.');
        }

        channel.state = mergeStates(channel.state, result.data || {});
        replaceChannelItems(channel, result.data?.items_html);
        channel.error = '';
        publishState(channel);
        onSuccess?.();
    };

    channel.queue = channel.queue.then(run, run).catch((error) => {
        if (!silent) {
            channel.error = error.message || 'Notification state could not be saved.';
        }
        syncChannel(channel);
    });
}

function scheduleChannelSync(channel) {
    if (channel.syncScheduled) {
        return;
    }

    channel.syncScheduled = true;
    const refresh = () => {
        if (document.visibilityState === 'visible') {
            queueMutation(channel, 'sync', {}, null, true);
        }

        channel.syncTimer = window.setTimeout(refresh, 30000);
    };

    channel.syncTimer = window.setTimeout(refresh, 30000);
}

function getChannel(center) {
    const key = center.dataset.channelKey;
    let channel = channels.get(key);

    if (!channel) {
        channel = {
            key,
            ajaxUrl: center.dataset.ajaxUrl,
            action: center.dataset.ajaxAction,
            nonce: center.dataset.ajaxNonce,
            state: stateFromCenter(center),
            views: new Set(),
            queue: Promise.resolve(),
            error: '',
            activeView: null,
            legacyScheduled: false,
            syncScheduled: false,
            syncTimer: null,
            broadcast: typeof BroadcastChannel === 'function'
                ? new BroadcastChannel(`growtype-notifications-${key}`)
                : null,
        };

        channel.broadcast?.addEventListener('message', (event) => {
            channel.state = mergeStates(channel.state, normalizeState(event.data));
            syncChannel(channel);
        });

        channels.set(key, channel);
    } else {
        channel.state = mergeStates(channel.state, stateFromCenter(center));
    }

    return channel;
}

function migrateLegacyState(center, channel) {
    if (channel.legacyScheduled) {
        return;
    }

    channel.legacyScheduled = true;
    const storageKey = center.dataset.legacyStorageKey;
    const legacyState = readLegacyState(storageKey);

    if (!legacyState) {
        return;
    }

    channel.state = mergeStates(channel.state, legacyState);
    publishState(channel);
    queueMutation(channel, 'import', legacyState, () => safeStorage.local.remove(storageKey));
}

function destroyNotificationCenter(center) {
    const view = viewsByCenter.get(center);

    if (!view) {
        return;
    }

    const wasOpen = !view.panel.hidden && view.channel.activeView === view;
    view.channel.views.delete(view);
    viewsByCenter.delete(center);
    delete center.dataset.notificationCenterInitialized;

    const panelIsStillUsed = [...view.channel.views]
        .some((remainingView) => remainingView.panel === view.panel);

    if (!panelIsStillUsed) {
        view.panel.remove();
    }

    if (view.channel.activeView === view) {
        view.channel.activeView = null;
    }

    if (wasOpen && view.channel.views.size > 0) {
        const remainingViews = [...view.channel.views];
        const replacement = remainingViews.find(({ trigger }) => trigger.offsetParent !== null)
            || remainingViews[0];
        replacement.setOpen(true);
    }

    if (view.channel.views.size === 0) {
        window.clearTimeout(view.channel.syncTimer);
        view.channel.broadcast?.close();
        channels.delete(view.channel.key);
    }
}

function positionPanel(view) {
    const triggerRect = view.trigger.getBoundingClientRect();
    const viewportInset = 12;
    const panelGap = 12;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const spaceAbove = Math.max(0, triggerRect.top - panelGap - viewportInset);
    const spaceBelow = Math.max(0, window.innerHeight - triggerRect.bottom - panelGap - viewportInset);
    const placeAbove = spaceAbove >= spaceBelow;
    const availableHeight = placeAbove ? spaceAbove : spaceBelow;

    view.panel.style.right = `${isMobile ? viewportInset : Math.max(viewportInset, window.innerWidth - triggerRect.right)}px`;
    view.panel.style.setProperty('--notification-panel-max-height', `${availableHeight}px`);
    view.panel.dataset.notificationPlacement = placeAbove ? 'above' : 'below';

    const panelHeight = view.panel.offsetHeight;
    const panelTop = placeAbove
        ? Math.max(viewportInset, triggerRect.top - panelGap - panelHeight)
        : triggerRect.bottom + panelGap;

    view.panel.style.top = `${panelTop}px`;
    view.panel.style.setProperty('--notification-panel-top', `${panelTop}px`);

    const panelWidth = view.panel.offsetWidth;
    const panelRight = Number.parseFloat(window.getComputedStyle(view.panel).right) || 0;
    const panelLeft = window.innerWidth - panelRight - panelWidth;
    const caretInset = 18;
    const caretLeft = Math.min(
        Math.max(triggerRect.left + (triggerRect.width / 2) - panelLeft, caretInset),
        panelWidth - caretInset,
    );
    view.panel.style.setProperty('--notification-panel-caret-left', `${caretLeft}px`);
}

function initNotificationCenter(center) {
    if (viewsByCenter.has(center)) {
        return;
    }

    const trigger = center.querySelector('.notification-center__trigger');
    const controlledPanelId = trigger?.getAttribute('aria-controls');
    const localPanel = center.querySelector('.notification-center__panel');
    const panel = localPanel
        || (controlledPanelId ? document.getElementById(controlledPanelId) : null);
    const count = center.querySelector('[data-notification-count]');
    const summary = panel?.querySelector('[data-notification-summary]');
    const empty = panel?.querySelector('[data-notification-empty]');
    const list = panel?.querySelector('[data-notification-list]');

    if (!center.dataset.channelKey
        || !center.dataset.ajaxUrl
        || !center.dataset.ajaxAction
        || !center.dataset.ajaxNonce
        || !trigger
        || !panel
        || !count
        || !summary
        || !empty
        || !list) {
        return;
    }

    center.dataset.notificationCenterInitialized = 'true';
    const channel = getChannel(center);
    panel.dataset.notificationPanelPortal = 'true';
    if (localPanel) {
        document.body.appendChild(panel);
    }
    const view = {
        center,
        channel,
        trigger,
        panel,
        count,
        summary,
        empty,
        list,
        items: [...panel.querySelectorAll('[data-notification-item]')],
    };
    channel.views.add(view);

    const setOpen = (isOpen, restoreFocus = false) => {
        if (isOpen) {
            panel.hidden = false;
            channel.views.forEach((otherView) => {
                if (otherView === view) {
                    return;
                }

                otherView.trigger.setAttribute('aria-expanded', 'false');
                otherView.center.classList.remove('is-open');
            });
            channel.activeView = view;
            positionPanel(view);
        }

        panel.hidden = !isOpen;
        trigger.setAttribute('aria-expanded', String(isOpen));
        center.classList.toggle('is-open', isOpen);

        if (!isOpen && channel.activeView === view) {
            channel.activeView = null;
        }

        if (isOpen) {
            const visibleIds = view.items
                .filter((item) => !item.hidden && item.dataset.notificationStateManaged !== '0')
                .map((item) => item.dataset.notificationId)
                .filter((id) => !channel.state.read.includes(id));

            if (visibleIds.length) {
                channel.state.read = normalizeIds([...channel.state.read, ...visibleIds]);
                publishState(channel);
                queueMutation(channel, 'read', { ids: visibleIds });
            }

            panel.focus();
        } else if (restoreFocus) {
            trigger.focus();
        }
    };

    view.setOpen = setOpen;
    viewsByCenter.set(center, view);

    trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        setOpen(panel.hidden);
    });

    if (!initializedPanels.has(panel)) {
        initializedPanels.add(panel);

        panel.querySelector('[data-notification-close]')
            ?.addEventListener('click', () => channel.activeView?.setOpen(false, true));

        panel.addEventListener('click', (event) => {
            const dismissButton = event.target.closest('[data-notification-dismiss]');

            if (!dismissButton) {
                return;
            }

            const id = dismissButton.closest('[data-notification-item]')?.dataset.notificationId;
            if (!id || channel.state.dismissed.includes(id)) {
                return;
            }

            channel.state.dismissed = normalizeIds([...channel.state.dismissed, id]);
            channel.state.read = channel.state.read.filter((readId) => readId !== id);
            publishState(channel);
            queueMutation(channel, 'dismiss', { ids: [id] });
        });

        panel.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !panel.hidden) {
                event.preventDefault();
                channel.activeView?.setOpen(false, true);
            }
        });
    }

    syncView(view);
    migrateLegacyState(center, channel);
    scheduleChannelSync(channel);
}

function notificationCenter() {
    const initWithin = (root = document) => {
        if (root.matches?.('[data-notification-center]')) {
            initNotificationCenter(root);
        }

        root.querySelectorAll?.('[data-notification-center]').forEach(initNotificationCenter);
    };

    const destroyWithin = (root) => {
        if (root.matches?.('[data-notification-center]')) {
            destroyNotificationCenter(root);
        }

        root.querySelectorAll?.('[data-notification-center]').forEach(destroyNotificationCenter);
    };

    const init = () => {
        initWithin();

        document.addEventListener('click', (event) => {
            const trigger = event.target.closest('.notification-center__trigger');
            const center = trigger?.closest('[data-notification-center]');

            if (center) {
                event.stopPropagation();
                initNotificationCenter(center);
                const view = viewsByCenter.get(center);
                view?.setOpen(view.panel.hidden);
            }

            channels.forEach((channel) => {
                const activeView = channel.activeView;

                if (activeView
                    && activeView.center !== center
                    && !activeView.panel.hidden
                    && !activeView.center.contains(event.target)
                    && !activeView.panel.contains(event.target)) {
                    activeView.setOpen(false);
                }
            });
        });

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.removedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                        destroyWithin(node);
                    }
                });

                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                        initWithin(node);
                    }
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
        return;
    }

    init();
}

export { notificationCenter };
